import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Progress, Typography, Space, Card, Alert } from "antd";
import { SendOutlined, ClearOutlined, RobotOutlined, LoadingOutlined } from "@ant-design/icons";
import { IconMessageCircle } from "@tabler/icons-react";
import { SetupTips } from "./setup-tips";
import { ChatMessageComponent, LoadingMessage } from "./chat-message";

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

// Type for chat messages
interface ChatMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
}

// Chrome's native LanguageModel types
interface LanguageModelSession {
    prompt: (text: string) => Promise<string>;
    promptStreaming: (text: string) => ReadableStream;
    clone: () => Promise<LanguageModelSession>;
    destroy: () => void;
    inputQuota: number;
}

interface LanguageModelCreateOptions {
    initialPrompts?: Array<{ role: "system" | "user" | "assistant"; content: string }>;
    monitor?: (monitor: any) => void;
}

// Global LanguageModel interface
declare global {
    interface Window {
        LanguageModel?: {
            create: (options?: LanguageModelCreateOptions) => Promise<LanguageModelSession>;
        };
    }
}

export const AIIntegration: React.FC = () => {
    const [isAIEnabled, setIsAIEnabled] = useState<boolean>(false);
    const [inputText, setInputText] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [session, setSession] = useState<LanguageModelSession | null>(null);
    const [error, setError] = useState<string>("");
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [downloadProgress, setDownloadProgress] = useState<number>(0);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Setup tips for Chrome AI configuration
    const setupTips = [
        "Make sure you're using Chrome 137+ (check chrome://version/)",
        "Go to chrome://flags/#prompt-api-for-gemini-nano",
        "Enable the 'Prompt API for Gemini Nano' flag",
        "Restart Chrome after enabling the flag",
        "Refresh this page to download the Gemini Nano model (~2GB)",
        "Wait for the model download to complete (may take a few minutes)",
    ];

    // Usage tips when AI is ready
    const usageTips = [
        "Press Enter to send messages, Shift+Enter for new lines",
        "Each conversation maintains context within the session",
        "The model has a 6,144 token context window limit",
        "Use 'Clear Chat' to start a fresh conversation",
        "Try asking questions, requesting explanations, or creative writing",
        "The AI runs entirely in your browser - no data sent to servers",
    ];

    useEffect(() => {
        // Initialize Chrome's native LanguageModel API
        const initializeAI = async () => {
            setError("");

            // Check if LanguageModel API is available
            if (!window.LanguageModel) {
                setError(
                    "Chrome's LanguageModel API is not available. Please ensure you're using Chrome 137+ with the Prompt API flag enabled.",
                );
                setIsAIEnabled(false);
                return;
            }

            try {
                setIsDownloading(true);

                // Create session with system prompt and download monitoring
                const aiSession = await window.LanguageModel.create({
                    initialPrompts: [
                        {
                            role: "system",
                            content:
                                "You are a helpful AI assistant. Provide clear, concise, and accurate responses. Be conversational and friendly.",
                        },
                    ],
                    monitor: (m) => {
                        m.addEventListener("downloadprogress", (e: any) => {
                            const progress = Math.round(e.loaded * 100);
                            setDownloadProgress(progress);
                            console.log(`Downloaded ${progress}%`);
                        });
                    },
                });

                console.log("LanguageModel session created successfully");
                console.log(`Input quota: ${aiSession.inputQuota} tokens`);

                setSession(aiSession);
                setIsAIEnabled(true);
                setIsDownloading(false);
            } catch (error: any) {
                console.error("Failed to initialize LanguageModel:", error);
                setError(
                    `Failed to initialize AI: ${error.message}. Make sure Chrome flags are enabled and the model is downloaded.`,
                );
                setIsAIEnabled(false);
                setIsDownloading(false);
            }
        };

        initializeAI();

        // Cleanup function
        return () => {
            if (session) {
                session.destroy();
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        const scrollToBottom = () => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        };
        scrollToBottom();
    }, [messages, isLoading]);

    const handleSendMessage = async (): Promise<void> => {
        if (!inputText.trim() || !session || isLoading) return;

        const userMessage: ChatMessage = {
            id: `user-${Date.now()}`,
            role: "user",
            content: inputText.trim(),
            timestamp: new Date(),
        };

        // Add user message to chat
        setMessages((prev) => [...prev, userMessage]);
        setInputText("");
        setIsLoading(true);
        setError("");

        try {
            // Use stateless approach by cloning the session for each request
            const clonedSession = await session.clone();
            const response = await clonedSession.prompt(userMessage.content);

            // Clean up the cloned session
            clonedSession.destroy();

            // Add AI response to chat
            const aiMessage: ChatMessage = {
                id: `ai-${Date.now()}`,
                role: "assistant",
                content: response,
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, aiMessage]);
        } catch (error: any) {
            console.error("Error generating response:", error);
            setError(error.message || "Failed to generate response. Please try again.");

            // Add error message to chat
            const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                role: "assistant",
                content: `Error: ${error.message || "Failed to generate response. Please try again."}`,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        setMessages([]);
        setError("");
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setInputText(e.target.value);
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-4xl mx-auto">
                <Card className="shadow-lg">
                    <div className="text-center mb-8">
                        <Space direction="vertical" size="small">
                            <RobotOutlined className="text-4xl text-blue-600" />
                            <Title level={2} className="!mb-2">
                                Chrome AI Chat
                            </Title>
                            <Paragraph className="text-lg text-gray-600 !mb-0">
                                Chat with Gemini Nano using Chrome's native LanguageModel API
                            </Paragraph>
                        </Space>
                    </div>

                    {/* Status Banner */}
                    <Space direction="vertical" size="middle" className="w-full mb-6">
                        {error && (
                            <>
                                <Alert
                                    message="Chrome AI Not Available"
                                    description={error}
                                    type="error"
                                    showIcon
                                    className="rounded-lg"
                                />
                                <SetupTips tips={setupTips} title="How to Enable Chrome AI" variant="warning" />
                            </>
                        )}

                        {isDownloading && (
                            <Alert
                                message="Downloading Gemini Nano Model..."
                                description={
                                    <div className="mt-2">
                                        <Paragraph className="!mb-2">
                                            Progress: {downloadProgress}% (This may take a few minutes on first use)
                                        </Paragraph>
                                        <Progress percent={downloadProgress} status="active" />
                                    </div>
                                }
                                type="info"
                                icon={<LoadingOutlined spin />}
                                showIcon
                                className="rounded-lg"
                            />
                        )}

                        {!isAIEnabled && !error && !isDownloading ? (
                            <Alert
                                message="Initializing Chrome AI..."
                                description="Setting up the AI chat interface. Please wait..."
                                type="warning"
                                icon={<LoadingOutlined spin />}
                                showIcon
                                className="rounded-lg"
                            />
                        ) : isAIEnabled ? (
                            <>
                                <Alert
                                    message="Gemini Nano Ready"
                                    description={`AI chat is ready! Context window: ${
                                        session?.inputQuota || "Unknown"
                                    } tokens`}
                                    type="success"
                                    showIcon
                                    className="rounded-lg"
                                />
                                <SetupTips tips={usageTips} title="How to Use Chrome AI Chat" variant="success" />
                            </>
                        ) : null}
                    </Space>

                    {/* Chat Messages */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <Title level={4} className="!mb-0">
                                Conversation
                            </Title>
                            {messages.length > 0 && (
                                <Button type="text" icon={<ClearOutlined />} onClick={clearChat} size="small">
                                    Clear Chat
                                </Button>
                            )}
                        </div>

                        <Card
                            className="h-[800px] flex flex-col"
                            bodyStyle={{ padding: 0, height: "100%", display: "flex", flexDirection: "column" }}
                        >
                            <div
                                className="flex-1 overflow-y-auto p-4 scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                                style={{ maxHeight: "100%" }}
                            >
                                {messages.length === 0 ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                            <IconMessageCircle size={48} className="mx-auto text-gray-400 mb-4" />
                                            <Paragraph className="text-gray-500 !mb-2">
                                                Start a conversation with Gemini Nano!
                                            </Paragraph>
                                            <Paragraph className="text-sm text-gray-400 !mb-0">
                                                Type your message below and press Enter or click Send.
                                            </Paragraph>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="pb-2">
                                        {messages.map((message) => (
                                            <ChatMessageComponent key={message.id} message={message} />
                                        ))}
                                        {isLoading && <LoadingMessage />}
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>

                    {/* Input Area */}
                    <div className="border-t border-gray-200 pt-4">
                        <Space.Compact className="w-full">
                            <TextArea
                                value={inputText}
                                onChange={handleInputChange}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
                                rows={3}
                                disabled={!isAIEnabled || isLoading || isDownloading}
                                className="flex-1"
                            />
                            <Button
                                type="primary"
                                icon={isLoading ? <LoadingOutlined spin /> : <SendOutlined />}
                                onClick={handleSendMessage}
                                disabled={!isAIEnabled || !inputText.trim() || isLoading || isDownloading}
                                size="large"
                                className="h-auto"
                            >
                                {isLoading ? "Sending..." : "Send"}
                            </Button>
                        </Space.Compact>
                    </div>
                </Card>
            </div>
        </div>
    );
};
